import type { ConversationAgentSummary } from "~/services/agents";
import { AgentSwitchMarker } from "./AgentSwitchMarker";
import { AssistantTurn } from "./AssistantTurn";
import { HandoffSuggestion } from "./HandoffSuggestion";
import { UserMessage } from "./UserMessage";
import type { AnswerChannel } from "./awaitingInput";
import { turnAnchorId } from "./useAnchoredScroll";
import type { ToolCallEntry, Turn } from "./useTurns";

// The peer recommended for the next turn is read from the turn's own
// `suggest_agent` tool call; of several such calls in a turn the last usable
// one (per `usableAgentIds`) wins. A call the backend rejected with a ModelRetry
// carries no outcome mark on the live stream, so its three rejections are
// mirrored here instead: off-roster and disabled picks fall outside
// `usableAgentIds`, and a pick of the turn's own handler is skipped.
const SUGGEST_AGENT_TOOL = "suggest_agent";

function parseSuggestedAgentId(args: string): string | undefined {
  try {
    const parsed = JSON.parse(args) as { agent_id?: unknown };
    return typeof parsed.agent_id === "string" ? parsed.agent_id : undefined;
  } catch {
    return undefined;
  }
}

function suggestedAgentIdFromToolCalls(
  toolCalls: readonly ToolCallEntry[],
  usableAgentIds: ReadonlySet<string> | undefined,
  handlerAgentId: string | undefined
): string | undefined {
  if (!usableAgentIds) return undefined;
  let suggested: string | undefined;
  for (const call of toolCalls) {
    if (call.name !== SUGGEST_AGENT_TOOL) continue;
    const agentId = parseSuggestedAgentId(call.args);
    if (agentId !== undefined && agentId !== handlerAgentId && usableAgentIds.has(agentId)) {
      suggested = agentId;
    }
  }
  return suggested;
}

interface ConversationTurnsProps {
  turns: Turn[];
  // Conversation-level agent identity for assistant avatars and turn labels;
  // null for legacy agentless conversations. Narrowed to the label + avatar
  // subset so read-only views can supply it without the agent's full config or
  // author metadata. Used for a turn with no recorded handler at all (a legacy
  // agentless row) — never to stand in for a handler that has since left the
  // project, which renders with no borrowed identity instead.
  conversationAgent: ConversationAgentSummary | null;
  // Resolves a turn's `agentId` to the agent that handled it, so each turn is
  // labeled with its own handler rather than the conversation's current bound
  // agent. A recorded id that does not resolve here (a since-detached handler)
  // renders unlabeled rather than borrowing the bound agent. Omitted by
  // read-only views, which carry the per-turn `agent` summary instead.
  agentsById?: ReadonlyMap<string, ConversationAgentSummary>;
  // The caller's usable (attached + enabled) agent ids — gates the latest
  // turn's handoff suggestion so a recommendation naming an agent outside it
  // (since disabled or detached) is never offered. Omitted by read-only
  // views, which offer no accept action at all.
  usableAgentIds?: ReadonlySet<string>;
  // Accepts the latest turn's handoff suggestion: the live view switches to the
  // agent and re-poses the message that produced it. Omitted by read-only views.
  onAcceptSuggestion?: (agentId: string) => void;
  // Dismisses the latest turn's handoff suggestion, keyed on that turn's id so a
  // later turn's new suggestion still shows. The live view records the dismissal
  // in sessionStorage. Omitted by read-only views.
  onDismissSuggestion?: (turnId: string) => void;
  // The suggesting-turn id the caller has dismissed (from sessionStorage). When it
  // matches the latest turn, the suggestion is hidden. Omitted by read-only views.
  dismissedSuggestionTurnId?: string | null;
  // The conversation's project, whose agent roster fills the handoff suggestion
  // card with the suggested peer's capability. Omitted by read-only views.
  projectId?: string;
  conversationId: string;
  // Live-only: surfaces the "Stopping" label on the in-flight turn.
  isCancelling?: boolean;
  // Read-only feedback rendering (admin review).
  readOnlyFeedback?: boolean;
  // Resume a paused human-in-the-loop tool call. Omitted in read-only views,
  // where the cards render but cannot be answered.
  onAnswer?: (toolCallId: string, answer: string) => void | Promise<void>;
  // Tool-call ids genuinely paused awaiting human input, with the channel that
  // answers each; gates answerability of the HITL and fallback cards.
  awaitingToolCalls?: ReadonlyMap<string, AnswerChannel>;
  // Turn id a deep-link scroll just landed on; flashes that user message.
  highlightedTurnId?: string | null;
  // Reveal the hover copy-link-to-turn affordance on assistant turns. On for
  // the read-only shared/admin views; off for the live regular conversation.
  showTurnLinks?: boolean;
}

// The single user/assistant turn dispatch shared by the live `Chat` and the
// read-only (shared / admin) views. Keeping it in one place means new turn
// kinds and per-turn rendering rules reach every surface automatically rather
// than having to be mirrored into each caller. Callers own their own scroll
// container and `<ol>` wrapper.
export function ConversationTurns({
  turns,
  conversationAgent,
  agentsById,
  usableAgentIds,
  onAcceptSuggestion,
  onDismissSuggestion,
  dismissedSuggestionTurnId,
  projectId,
  conversationId,
  isCancelling,
  readOnlyFeedback,
  onAnswer,
  awaitingToolCalls,
  highlightedTurnId,
  showTurnLinks,
}: ConversationTurnsProps) {
  // The pending suggestion is the latest turn's alone — a later turn (or a
  // reply still streaming) supersedes it, whether or not it was accepted. It is
  // read from that turn's own `suggest_agent` tool call; a failed or cancelled
  // turn is excluded, matching the tool's success-only intent.
  const lastTurn = turns[turns.length - 1];
  const suggestedAgentId =
    onAcceptSuggestion &&
    lastTurn?.kind === "assistant" &&
    !lastTurn.isRunning &&
    !lastTurn.failed &&
    !lastTurn.cancelled
      ? suggestedAgentIdFromToolCalls(lastTurn.toolCalls, usableAgentIds, lastTurn.agentId)
      : undefined;
  // The suggesting turn's id keys a turn-scoped dismissal: dismissing this
  // suggestion hides it (here and across a reload) without suppressing a later
  // turn's new one. A completed assistant turn that carries a suggestion always
  // carries this id too (both ride the same turn), so requiring it below never
  // hides a real suggestion — it just ensures the card, which always offers a
  // Dismiss action, is only shown once that dismissal can be wired.
  const suggestingTurnId = lastTurn?.kind === "assistant" ? lastTurn.turnId : undefined;
  const dismissed = !!suggestingTurnId && suggestingTurnId === dismissedSuggestionTurnId;
  return (
    <>
      {turns.map((turn) => {
        if (turn.kind === "user") {
          const anchorId = turnAnchorId(turn.turnId);
          return (
            <UserMessage
              key={turn.key}
              text={turn.text}
              attachmentIds={turn.attachmentIds}
              turnId={anchorId}
              highlighted={!!anchorId && anchorId === highlightedTurnId}
            />
          );
        }
        if (turn.kind === "agent-switch") {
          const fromName =
            turn.fromAgent?.display_name ??
            (turn.fromAgentId ? agentsById?.get(turn.fromAgentId)?.display_name : undefined);
          const toName =
            turn.toAgent?.display_name ??
            (turn.toAgentId ? agentsById?.get(turn.toAgentId)?.display_name : undefined);
          return <AgentSwitchMarker key={turn.key} fromAgent={fromName} toAgent={toName} />;
        }
        // A recorded `agentId` that no longer resolves against the roster is an
        // agent detached from the project since it took this turn: render it with
        // no borrowed identity rather than the conversation's current bound agent
        // — mirroring the backend's "a different agent no longer in this project"
        // degrade, never claiming a departed peer's turn for whoever is bound now.
        // Only a turn with no recorded handler (a legacy agentless row) falls back
        // to the conversation agent; shared reads carry `turn.agent` and skip this.
        const turnAgent = turn.agentId
          ? (agentsById?.get(turn.agentId) ?? null)
          : conversationAgent;
        return (
          <AssistantTurn
            key={turn.key}
            turn={turn}
            agent={turn.agent ?? turnAgent}
            conversationId={conversationId}
            isCancelling={isCancelling}
            readOnlyFeedback={readOnlyFeedback}
            onAnswer={onAnswer}
            awaitingToolCalls={awaitingToolCalls}
            showTurnLinks={showTurnLinks}
          />
        );
      })}
      {suggestedAgentId && !dismissed && suggestingTurnId && onDismissSuggestion && (
        <HandoffSuggestion
          projectId={projectId}
          agentId={suggestedAgentId}
          agentName={agentsById?.get(suggestedAgentId)?.display_name ?? "This agent"}
          onAccept={() => onAcceptSuggestion?.(suggestedAgentId)}
          onDismiss={() => onDismissSuggestion(suggestingTurnId)}
        />
      )}
    </>
  );
}
